import i18next, {
  type i18n as I18Next,
  type InitOptions as I18NextInitOptions
} from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpBackend from 'i18next-http-backend'
import yaml from 'js-yaml'
import { initReactI18next } from 'react-i18next'

import envConfig from './env-config'

export type LanguageID = 'en' | 'zh'

export const languagesHashmap = {
  en: {
    id: 'en',
    name: 'English',
    resourceId: 'en'
  },
  zh: {
    id: 'zh',
    name: '简体中文',
    resourceId: 'cn'
  }
}
export const languages = Object.keys(languagesHashmap)

type NestedRecord = { [key: string]: NestedRecord } | string

interface I18nInitOptions {
  remoteNamespaces?: string[]
  remoteLazyLoad?: boolean
  useHttpBackend?: boolean
}

export const initI18n = (options?: I18nInitOptions) => {
  const i18n = i18next.createInstance()

  // Ensure hook will be called after i18next is initialized.
  function onI18nInitialized(hook: (i18nInstance: I18Next) => any) {
    if (i18n.isInitialized) {
      hook(i18n)
    } else {
      i18n.on('initialized', () => hook(i18n))
    }
  }

  /**
   * Add translations to i18next.
   * @param translations Translations in json format
   * @param namespace i18next namespace
   */
  function addTranslations(
    translations: Partial<Record<LanguageID, { [key: string]: NestedRecord }>>,
    namespace = 'translation'
  ) {
    onI18nInitialized((i18nInstance: I18Next) =>
      Object.keys(translations).forEach((lang) => {
        if (languages.includes(lang)) {
          i18nInstance.addResourceBundle(
            lang,
            namespace,
            translations[lang as LanguageID],
            false,
            false
          )
        }
      })
    )
  }

  const remoteNamespaces = Array.isArray(options?.remoteNamespaces)
    ? options?.remoteNamespaces!
    : []
  const { remoteLazyLoad = true, useHttpBackend = false } = options || {}

  const initialNamespaces = useHttpBackend
    ? ['translation', ...(remoteLazyLoad ? [] : remoteNamespaces)]
    : ['translation']

  const httpBackendOptions = {
    loadPath: (lngs: string[], namespaces: string[]) => {
      if (!remoteNamespaces?.includes(namespaces[0])) {
        return ''
      }

      const lang = lngs[0] as LanguageID
      const { resourceId } = languagesHashmap[lang]

      return `${envConfig.i18nStoreOrigin}/{{ns}}/${resourceId}.yaml`
    },
    parse: (data: string) => {
      try {
        return yaml.load(data) as any
      } catch (e) {
        console.log('Failed to parse yaml: ', e)
        return {}
      }
    },
    requestOptions: {
      cache: 'no-cache'
    }
  }

  const i18nWithPlugins = useHttpBackend ? i18n.use(HttpBackend) : i18n

  i18nWithPlugins
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      fallbackLng: languages[0],
      supportedLngs: languages,
      load: 'languageOnly',
      preload: languages,
      ns: initialNamespaces,
      interpolation: {
        escapeValue: false
      },
      partialBundledLanguages: true,
      detection: {
        lookupQuerystring: 'lang',
        lookupCookie: 'lang',
        lookupLocalStorage: 'lang',
        lookupSessionStorage: 'lang'
      },
      ...(useHttpBackend
        ? {
            backend: httpBackendOptions as I18NextInitOptions['backend']
          }
        : {})
    })

  return {
    i18n,
    addTranslations
  }
}
